/**
 * Redux actions for individual assets.
 * @module redux/actions/asset
 */

import { createAction } from "redux-actions";
import { push } from "react-router-redux";
import uuid from "uuid";
import TemplateUtils from "../util/templateUtils";
import { parseMosMessageXml } from "../util/mosUtils";
import { getNcsIdFromNcsAppInfo } from "../util/ncsUtils";
import { getPrimeStyles as apiGetPrimeStyles } from "../api/primeStyles";
import {
  getAssets as apiGetAssets,
  getTemplateDefinition as apiGetTemplateDefinition,
  getAutomationTransitions as apiGetAutomationTransitions,
  getVirtualChannels as apiGetVirtualChannels,
  getVirtualChannelsByAssetId as apiGetVirtualChannelsByAssetId,
  createMosObject as apiInputMosObject,
  getMosObjectTemplate as apiGetMosObjectTemplateTemplate,
  getMosObjectTemplatePreviewPath as apiGetMosObjectTemplateTemplatePreviewPath,
  generatePreview as apiGeneratePreview,
  createDeficientAsset as apiCreateDeficientAsset,
  getMosObject as apiGetMosObject
} from "../api/camio";
import { xmlToJson, jsonToXml } from "../util/xmlUtils";
import { sendMosMessageToContainer } from "../api/container";
import {
  generateDefaultEmptyAutofillOption,
  getDefaultAutofillOptions,
  getDefaultAutofillColumnOptions
} from "../util/autofillUtils";
import {
  getAutofillData as apiGetAutofillData,
  getAutofillMedia as apiGetAutofillMedia,
  getAutofillColumnData as apiGetAutofillColumnData
} from "../api/autofill";
import { getLuciPanel } from "./luciPanel";
import { actions as snackActions } from "./snackbar";
import { actions as appActions } from "./app";
import AppMode from "../constants/appMode";
import localize from "../util/localizeUtils";

export const ASSET_SELECTED = "ASSET/ASSET_SELECTED";
export const LOAD_ASSET_REQUEST = "ASSET/LOAD_ASSET_REQUEST";
export const ASSET_LOADED = "ASSET/ASSET_LOADED";
export const ASSET_LOAD_FAILED = "ASSET/ASSET_LOAD_FAILED";
export const CLEAR_ASSET = "ASSET/CLEAR";
export const TEMPLATE_DEFINITION_LOADED = "ASSET/TEMPLATE_DEFINITION_LOADED";
export const UPDATE_REPLACEABLE_VALUE = "ASSET/UPDATE_REPLACEABLE_VALUE";
export const UPDATE_TEMPLATE_PROPERTY = "ASSET/UPDATE_TEMPLATE_PROPERTY";
export const ASSET_LOAD_REQUEST_FAILED = "ASSET/ASSET_LOAD_REQUEST_FAILED";
export const CHANNEL_LIST_RECEIVED = "ASSET/CHANNEL_LIST_RECEIVED";
export const INSERT_MOS_OBJECT_REQUEST = "ASSET/INSERT_MOS_OBJECT_REQUEST";
export const INSERT_MOS_OBJECT_FAILED = "ASSET/INSERT_MOS_OBJECT_FAILED";
export const INSERT_MOS_OBJECT_SUCCESS = "ASSET/INSERT_MOS_OBJECT_SUCCESS";
export const GENERATE_PREVIEW = "ASSET/GENERATE_PREVIEW";
export const PREVIEW_RECEIVED = "ASSET/PREVIEW_RECEIVED";
export const GENERATE_PREVIEW_FAILED = "ASSET/GENERATE_PREVIEW_FAILED";
export const SELECT_REPLACEABLE = "ASSET/SELECT_REPLACEABLE";
export const SET_AUTOFILL_DATA = "ASSET/SET_AUTOFILL_DATA";
export const SET_AUTOFILL_COLUMN_DATA = "ASSET/SET_AUTOFILL_COLUMN_DATA";
export const LOAD_AUTOFILL_REQUEST = "ASSET/LOAD_AUTOFILL_REQUEST";
export const END_EDIT_ASSET = "ASSET/END_EDIT_ASSET";
export const MOS_OBJECT_PREVIEW_PATH_RETRIEVED =
  "ASSET/MOS_OBJECT_PREVIEW_PATH_RETRIEVED";
export const AUTOMATION_TRANSITIONS_RECEIVED =
  "ASSET/AUTOMATION_TRANSITIONS_RECEIVED";
export const TOGGLE_REPLACEABLE_USE_DATA_SOURCE =
  "ASSET/TOGGLE_REPLACEABLE_USE_DATA_SOURCE";
export const UPDATE_AUTOFILL_REPLACEABLE_VALUES =
  "ASSET/UPDATE_AUTOFILL_REPLACEABLE_VALUES";
export const UPDATE_DATA_BOUND_REPLACEABLE_VALUES =
  "ASSET/UPDATE_DATA_BOUND_REPLACEABLE_VALUES";
export const BEGIN_TEMPLATE_SWAP = "ASSET/BEGIN_TEMPLATE_SWAP";
export const END_TEMPLATE_SWAP = "ASSET/END_TEMPLATE_SWAP";
export const SWAP_TEMPLATE_REPLACEABLE_VALUES =
  "ASSET/SWAP_TEMPLATE_REPLACEABLE_VALUES";
export const SET_STORED_MOS_XML = "ASSET/SET_STORED_MOS_XML";
export const REQUEST_PRIME_STYLES = "ASSETS/REQUEST_PRIME_STYLES";
export const REQUEST_PRIME_STYLES_FAILED = "ASSETS/REQUEST_PRIME_STYLES_FAILED";
export const PRIME_STYLES_RECEIVED = "ASSETS/PRIME_STYLES_RECEIVED";
export const PRIME_STYLE_CHANGED = "ASSETS/PRIME_STYLE_CHANGED";
export const RESTORE_STATE = "ASSET/RESTORE_STATE";

export const storeMosXml = mosXml =>
  createAction(SET_STORED_MOS_XML)({ mosXml });

export const toggleReplaceableUseDataSource = id =>
  createAction(TOGGLE_REPLACEABLE_USE_DATA_SOURCE)({ id });

export const clearAsset = () => createAction(CLEAR_ASSET)();

const insertMosObjectSuccess = (
  mosXmlPayload,
  requestData,
  suppressSnack
) => dispatch => {
  dispatch(
    createAction(INSERT_MOS_OBJECT_SUCCESS)({
      ...mosXmlPayload,
      replaceables: requestData.replaceables,
      templateId: requestData.templateId
    })
  );
  if (!suppressSnack) {
    dispatch(
      snackActions.showSnack(uuid.v4(), {
        content: localize("mosItemSuccessFormat").replace(
          "{objId}",
          mosXmlPayload.objId
        ),
        timeout: 7000,
        buttonTemplate: localize("dismissButton")
      })
    );
  }
};

const insertMosObjectFailed = suppressSnack => dispatch => {
  dispatch(createAction(INSERT_MOS_OBJECT_FAILED)());

  if (!suppressSnack) {
    dispatch(
      snackActions.showSnack(uuid.v4(), {
        content: localize("mosObjectCreateError"),
        timeout: 7000,
        buttonTemplate: localize("dismissButton")
      })
    );
  }
};

export function selectAsset(id, editing, previousMosObject) {
  return (dispatch, getState) => {
    const results = getState().assets.assetList.filter(a => a.id === id);
    let selectedAsset = {};
    if (results.length > 0) {
      selectedAsset = results[0];
      return dispatch(
        createAction(ASSET_SELECTED)({
          selectedAsset,
          editing,
          previousMosObject,
          swapping: !!previousMosObject
        })
      );
    }
  };
}

const loadAssetRequest = createAction(LOAD_ASSET_REQUEST);
export const templateDefinitionLoaded = templateDefinition =>
  createAction(TEMPLATE_DEFINITION_LOADED)({ templateDefinition });
export const channelListReceived = channelList =>
  createAction(CHANNEL_LIST_RECEIVED)({ channelList });
const handleAssetLoadRequestFailed = createAction(ASSET_LOAD_REQUEST_FAILED);
export const updateReplaceableValue = data =>
  createAction(UPDATE_REPLACEABLE_VALUE)({
    type: data.type,
    id: data.id,
    value: data.value,
    name: data.name,
    url: data.url,
    provider: data.provider
  });
export const updateTemplateProperty = (key, value) =>
  createAction(UPDATE_TEMPLATE_PROPERTY)({ key, value });
export const selectReplaceable = (id, categoryId, restrictCategory) =>
  createAction(SELECT_REPLACEABLE)({ id, categoryId, restrictCategory });
const loadAutofillRequest = () => createAction(LOAD_AUTOFILL_REQUEST)();
const setAutofillData = data => createAction(SET_AUTOFILL_DATA)({ data });
const setAutofillColumnData = data =>
  createAction(SET_AUTOFILL_COLUMN_DATA)({ data });

export const updateSelectedReplaceable = (value, url, provider) => (
  dispatch,
  getState
) => {
  const currentState = getState().asset;
  const replaceable = (currentState.templateDefinition.textReplaceables || [])
    .concat(currentState.templateDefinition.mediaReplaceables || [])
    .find(r => r.id === currentState.selectedReplaceableId);

  if (replaceable) {
    return dispatch(
      updateReplaceableValue({
        id: replaceable.id,
        name: replaceable.name,
        type: replaceable.type,
        value,
        url,
        provider
      })
    );
  }
};

function handleTemplateDefinitionReceived(
  templateDefinitionXml,
  templateReplaceables
) {
  return dispatch => {
    const templateDefinition = TemplateUtils.parseTemplateDefinition(
      templateDefinitionXml,
      templateReplaceables
    );
    return dispatch(templateDefinitionLoaded(templateDefinition));
  };
}

export function loadChannelList() {
  return dispatch =>
    apiGetVirtualChannels()
      .then(channels => dispatch(channelListReceived(channels)))
      .catch(error => {
        console.error(error);
        return dispatch(handleAssetLoadRequestFailed());
      });
}

export function loadChannelListByAssetId(id) {
  return dispatch =>
    apiGetVirtualChannelsByAssetId(id)
      .then(channels => dispatch(channelListReceived(channels)))
      .catch(error => {
        console.error(error);
        return dispatch(handleAssetLoadRequestFailed());
      });
}

/**
 * Submits a request to the Luci backend service to retrieve assets
 * based on the current filtering options.
 * @param  {string} assetId The id of the asset to search.
 * replaceables with the specified categories
 */
export function getAsset(assetId) {
  return dispatch => {
    const payload = {
      assetId
    };

    apiGetAssets(payload)
      .then(response =>
        dispatch(createAction(ASSET_LOADED)({ asset: response }))
      )
      .catch(error => {
        console.error(error);
        dispatch(clearAsset());
        return dispatch(createAction(ASSET_LOAD_FAILED));
      });
  };
}

export function loadAsset(id, cancelRedirect, selectedContent) {
  return (dispatch, getState) =>
    Promise.all([
      dispatch(selectAsset(id)),
      dispatch(loadChannelListByAssetId(id)),
      dispatch(loadTemplateDefinition()),
      dispatch(getLuciPanel(id))
    ])
      .then(() => {
        if (!cancelRedirect) {
          dispatch(push(`/editor/${getState().asset.selectedAssetId}`));
        }
        if (selectedContent) {
          dispatch(
            insertSelectedContentIntoTemplate(
              selectedContent,
              getState().content.assetType
            )
          );
        }
        return Promise.resolve();
      })
      .catch(() => {
        dispatch(
          snackActions.showSnack(uuid.v4(), {
            content: "Failed to load asset.",
            timeout: 7000,
            buttonTemplate: "DISMISS"
          })
        );
      });
}

export function swapTemplate(id) {
  return (dispatch, getState) => {
    const currentState = getState().asset;
    const templateDefinition = currentState.templateDefinition;
    const previousMosObject = currentState.isMosObject
      ? currentState.asset
      : null;
    const replaceables = (templateDefinition.mediaReplaceables || []).concat(
      templateDefinition.textReplaceables
    );
    return Promise.all([
      dispatch(createAction(BEGIN_TEMPLATE_SWAP)()),
      dispatch(selectAsset(id, currentState.editing, previousMosObject)),
      dispatch(loadChannelListByAssetId(id)),
      dispatch(loadTemplateDefinition()),
      dispatch(getLuciPanel(id))
    ])
      .then(() => dispatch(insertReplaceablesIntoTemplate(replaceables)))
      .catch(() => {
        dispatch(
          snackActions.showSnack(uuid.v4(), {
            content: "Failed to load asset.",
            timeout: 7000,
            buttonTemplate: "DISMISS"
          })
        );
      });
  };
}

const automationTransitionListReceived = automationTransitionList =>
  createAction(AUTOMATION_TRANSITIONS_RECEIVED)({ automationTransitionList });

export function loadAutomationTransitionList() {
  return dispatch =>
    apiGetAutomationTransitions()
      .then(automationTransitions =>
        dispatch(automationTransitionListReceived(automationTransitions))
      )
      .catch(error => {
        console.error(error);
        return dispatch(handleAssetLoadRequestFailed());
      });
}

export function openMosObject(message) {
  const mosObject = parseMosMessageXml(message);
  if (mosObject.mosObjectId && mosObject.mosObjectId != "XXX") {
    return dispatch => {
      dispatch(loadAssetRequest());
      return apiGetMosObjectTemplateTemplate(mosObject.mosObjectId)
        .then(json => {
          dispatch(
            createAction(ASSET_SELECTED)({
              selectedAsset: mosObject,
              editing: true
            })
          );
          dispatch(
            handleTemplateDefinitionReceived(
              json.templateDefinition,
              json.templateReplaceables
            )
          );
          dispatch(loadAutofillData());

          return dispatch(
            push(
              `/editor/${mosObject.id ? mosObject.id : mosObject.mosObjectId}`
            )
          );
        })
        .catch(error => {
          console.error(error);
          return dispatch(handleAssetLoadRequestFailed());
        });
    };
  }
}

export function openClipperMosObject(message) {
  const mosObject = parseMosMessageXml(message);
  if (mosObject.mosObjectId) {
    return dispatch =>
      apiGetMosObjectTemplateTemplate(mosObject.mosObjectId)
        .then(json => {
          dispatch(
            createAction(ASSET_SELECTED)({
              selectedAsset: mosObject,
              editing: true
            })
          );
          dispatch(
            handleTemplateDefinitionReceived(
              json.templateDefinition,
              json.templateReplaceables
            )
          );
          return dispatch(loadAutofillData());
        })
        .catch(error => {
          console.error(error);
          return dispatch(handleAssetLoadRequestFailed());
        });
  }
}

export function generatePreview() {
  return (dispatch, getState) => {
    const currentState = getState().asset;

    if (currentState.generatingPreview) {
      return;
    }

    const requestData = {
      replaceables: (currentState.templateDefinition.textReplaceables || [])
        .concat(currentState.templateDefinition.mediaReplaceables)
        .map(r => ({
          id: r.id,
          name: r.name,
          type: r.type,
          value: r.value,
          provider: r.provider || "local",
          dataSource: r.properties.usesDataSource
            ? r.properties.dataSource
            : null
        })),
      assetId: !currentState.isMosObject ? currentState.asset.id : undefined,
      mosObjectId: currentState.isMosObject
        ? currentState.asset.mosObjectId
        : undefined,
      withPreviewOnlyImages: true
    };

    if (currentState.primeStyle) {
      requestData.style = currentState.primeStyle;
    }

    dispatch(createAction(GENERATE_PREVIEW)());

    apiGeneratePreview(requestData)
      .then(response => {
        if (
          response.updatedReplaceables &&
          response.updatedReplaceables.length > 0
        ) {
          dispatch(
            createAction(UPDATE_DATA_BOUND_REPLACEABLE_VALUES)({
              values: response.updatedReplaceables
            })
          );
        }
        if (response.previewImg) {
          return dispatch(
            createAction(PREVIEW_RECEIVED)({ previewImg: response.previewImg })
          );
        }
        return dispatch(
          createAction(GENERATE_PREVIEW_FAILED)({
            error: "Failed to generate preview"
          })
        );
      })
      .catch(error => {
        console.error(error);
        return dispatch(
          createAction(GENERATE_PREVIEW_FAILED)({
            error: "Failed to generate preview"
          })
        );
      });
  };
}

function insertSelectedContentIntoTemplate(selectedContent, type) {
  if (!selectedContent) {
    return;
  }

  return (dispatch, getState) => {
    const templateDefinition = getState().asset.templateDefinition;
    if (!templateDefinition) {
      return;
    }

    const matchingReplaceables = templateDefinition.mediaReplaceables.filter(
      r => r.type === type
    );
    if (matchingReplaceables.length > 0) {
      const firstReplaceable = matchingReplaceables[0];
      dispatch(
        updateReplaceableValue({
          ...firstReplaceable,
          value: selectedContent.id,
          url: selectedContent.thumbnailUrl,
          provider: selectedContent.provider
        })
      );
    }
  };
}

function insertReplaceablesIntoTemplate(replaceables) {
  if (!replaceables || replaceables.constructor !== Array) {
    return;
  }

  return (dispatch, getState) => {
    const templateDefinition = getState().asset.templateDefinition;
    if (!templateDefinition) {
      return;
    }

    const replaceablesToChange = (
      templateDefinition.textReplaceables || []
    ).concat(templateDefinition.mediaReplaceables);

    if (replaceablesToChange) {
      dispatch(
        createAction(SWAP_TEMPLATE_REPLACEABLE_VALUES)({ replaceables })
      );
      dispatch(createAction(END_TEMPLATE_SWAP)());
      dispatch(generatePreview());
    }
  };
}

function loadTemplateDefinition() {
  return (dispatch, getState) => {
    const assetsState = getState().assets;
    const currentState = getState().asset;

    const id = currentState.isMosObject
      ? currentState.asset.mosObjectId
      : currentState.asset.id;
    const searchType = currentState.isMosObject ? "mosobject" : "id";

    dispatch(loadAssetRequest());

    return apiGetTemplateDefinition(id, searchType, assetsState.activeView)
      .then(json => {
        dispatch(
          handleTemplateDefinitionReceived(
            json.templateDefinition,
            json.templateReplaceables
          )
        );
        return dispatch(loadAutofillData());
      })
      .catch(error => {
        console.error(error);
        dispatch(handleAssetLoadRequestFailed());
        return Promise.reject();
      });
  };
}

function loadDefaultAutofillData(dispatch, allReplaceables) {
  const autofillData = {};
  const defaultAutofillOptions = getDefaultAutofillOptions(allReplaceables);
  const autofillReplaceables = allReplaceables.filter(
    r => r.properties["data list xpath"]
  );
  return Promise.all(
    autofillReplaceables.map(r =>
      apiGetAutofillData(
        r.properties["data list name"],
        r.properties["data list xpath"],
        r.name
      ).then(data => {
        if (data == null) {
          return Promise.resolve(); // handles missing data list
        }
        const autofill = data;
        autofill.unshift(defaultAutofillOptions[r.name]);
        // add empty value to allow clearing
        if (defaultAutofillOptions[r.name][r.name].value !== "") {
          const emptyOption = generateDefaultEmptyAutofillOption(
            allReplaceables,
            r.name
          );
          autofill.unshift(emptyOption);
        }
        autofillData[r.name] = autofill.filter(d => d[r.name] != null);
        return Promise.resolve();
      })
    )
  )
    .then(() => dispatch(setAutofillData(autofillData)))
    .catch(error => {
      console.error(error);
      dispatch(setAutofillData({}));
      return Promise.reject();
    });
}

function loadColumnAutofillData(dispatch, replaceables) {
  const autofillData = {};
  const defaultAutofillOptions = getDefaultAutofillColumnOptions(replaceables);
  return Promise.all(
    replaceables.map(r => {
      const dataListXPath = r.properties.autofillDataListName
        .toLowerCase()
        .replace(/\/$/, "");
      return apiGetAutofillColumnData(dataListXPath).then(data => {
        if (data == null) {
          return Promise.resolve(); // handles missing data list
        }
        const autofill = data;
        // only add default value if it doesn't exist in list
        if (
          !autofill.some(a =>
            a.columns.some(
              c =>
                c.type === "txt" &&
                c.value === defaultAutofillOptions[r.name].columns[0].value
            )
          )
        ) {
          autofill.unshift(defaultAutofillOptions[r.name]);
        }
        // add empty value to allow clearing
        if (defaultAutofillOptions[r.name].columns[0].value !== "") {
          autofill.unshift({ columns: [{ type: "txt", value: "" }] });
        }
        autofillData[r.name] = autofill.filter(d => d.columns != null);
        return Promise.resolve();
      });
    })
  )
    .then(() => dispatch(setAutofillColumnData(autofillData)))
    .catch(error => {
      console.error(error);
      dispatch(setAutofillColumnData({}));
      return Promise.reject();
    });
}

function loadAutofillData() {
  return (dispatch, getState) => {
    const { templateDefinition, autofillLoading } = getState().asset;
    if (autofillLoading) {
      return;
    }
    dispatch(loadAutofillRequest());
    const allReplaceables = (templateDefinition.textReplaceables || []).concat(
      templateDefinition.mediaReplaceables
    );
    const autofillReplaceables = allReplaceables.filter(
      r => r.properties.autofillDataColumnIndex === 0
    );
    return autofillReplaceables.length === 0
      ? loadDefaultAutofillData(dispatch, allReplaceables)
      : loadColumnAutofillData(dispatch, autofillReplaceables);
  };
}

export function insertFromClipper(mosObjects) {
  return (dispatch, getState) => {
    let insertMosObject = () => {};
    if (getState().asset.editing) {
      insertMosObject = updateMosObject;
    } else if (getState().asset.isMosObject) {
      insertMosObject = createMosObjectFromExisting;
    } else {
      insertMosObject = createNewMosObject;
    }
    const additionalMosXml = mosObjects
      ? mosObjects.map(g => ({ mosObjectId: g.mosObjectId, mosXml: g.mosXml }))
      : null;

    return dispatch(insertMosObject({ additionalMosXml }));
  };
}

export const createNewMosObject = options => createMosObject("insert", options);

export const createMosObjectFromExisting = options =>
  createMosObject("insertexisting", options);

export const replaceMosObjectWithNew = options =>
  createMosObject("replace", options);

export const updateMosObject = options => createMosObject("update", options);

export function createMosObjectFromDragDrop(id) {
  return (dispatch, getState) => {
    let asset = null;
    let templateDefinition = null;
    const ncsId = getState().app.ncsId;
    let requestData = {};

    try {
      asset = getState().assets.assetList.find(x => x.id === id);
      templateDefinition = TemplateUtils.parseTemplateDefinition(
        asset.templateDefinitionXml,
        asset.templateReplaceables
      );
      const channel = getState().asset.channelList.find(
        c => c.name === templateDefinition.properties["default channel"]
      );
      const outTime = templateDefinition.properties["automation duration"];
      let inTime = templateDefinition.properties["automation start"];
      if (!!outTime && !inTime) {
        inTime = "00:00:00:00";
      }

      requestData = {
        mosObjectObjectId: asset.mosObjectId,
        itemId: asset.id,
        ncsId,
        replaceables: (templateDefinition.textReplaceables || [])
          .concat(templateDefinition.mediaReplaceables)
          .map(r => ({
            id: r.id,
            name: r.name,
            type: r.type,
            value: r.value,
            provider: r.provider,
            dataSource: r.properties.usesDataSource
              ? r.properties.dataSource
              : null
          })),
        templateData: {
          automationTransition:
            templateDefinition.properties["automation transition"],
          channelId: channel ? channel.id : "",
          channelName: templateDefinition.properties["default channel"],
          customMetadata: templateDefinition.customMetadata,
          layer: templateDefinition.properties.layer,
          inTime,
          outTime,
          slug: templateDefinition.properties["mos abstract"],
          style: getState().asset.primeStyle
        },
        templateId: templateDefinition.uuid
      };
    } catch (err) {
      return dispatch(
        snackActions.showSnack(uuid.v4(), {
          content: "Failed to insert dragged asset.",
          timeout: 7000,
          buttonTemplate: "DISMISS"
        })
      );
    }

    return apiInputMosObject(requestData, "insertexisting")
      .then(response => {
        const mosXmlPayload = getMosXmlPayload(response);
        if (mosXmlPayload) {
          sendMosMessageToContainer(mosXmlPayload.mosXml);

          return dispatch(insertMosObjectSuccess(mosXmlPayload, requestData));
        }
        return dispatch(insertMosObjectFailed());
      })
      .catch(error => {
        console.error(error);
        return dispatch(insertMosObjectFailed());
      });
  };
}

function createMosObject(createMethod, options) {
  // eslint-disable-next-line
  options = options || {};
  return (dispatch, getState) => {
    const currentState = getState().asset;
    const { ncsId, placeholderId } = getState().app;
    const templateId =
      createMethod === "insert" ||
      ((createMethod === "replace" || createMethod === "update") &&
        !currentState.isMosObject)
        ? currentState.asset.id
        : currentState.templateDefinition.uuid;
    const channel = currentState.channelList.find(
      c =>
        c.name === currentState.templateDefinition.properties["default channel"]
    );
    const outTime =
      currentState.templateDefinition.properties["automation duration"];
    const previousMosObject = currentState.previousMosObject || {};
    let inTime = currentState.templateDefinition.properties["automation start"];
    if (!!outTime && !inTime) {
      inTime = "00:00:00:00";
    }
    const requestData = {
      mosObjectObjectId:
        previousMosObject.mosObjectId || currentState.asset.mosObjectId || "",
      itemId:
        (options ? options.itemId : null) ||
        previousMosObject.id ||
        placeholderId ||
        currentState.asset.id,
      ncsId,
      replaceables: (currentState.templateDefinition.textReplaceables || [])
        .concat(currentState.templateDefinition.mediaReplaceables)
        .map(r => ({
          id: r.id,
          name: r.name,
          type: r.type,
          value: r.value,
          provider: r.provider,
          dataSource: r.properties.usesDataSource
            ? r.properties.dataSource
            : null
        })),
      templateData: {
        automationTransition:
          currentState.templateDefinition.properties["automation transition"],
        channelId: channel ? channel.id : "",
        channelName:
          currentState.templateDefinition.properties["default channel"],
        customMetadata: currentState.templateDefinition.customMetadata,
        layer: currentState.templateDefinition.properties.layer,
        inTime,
        outTime,
        slug: currentState.templateDefinition.properties["mos abstract"],
        style: currentState.primeStyle
      },
      templateId
    };

    if (options.includeRunningOrderInfo) {
      const runningOrder = getState().runningOrder;
      const selectedStory = runningOrder.runningOrder.stories.find(
        r => r.id === runningOrder.selectedStoryId
      );
      let selectedItem = null;
      requestData.roId = runningOrder.runningOrder.id;
      requestData.storyId = runningOrder.selectedStoryId;

      if (selectedStory) {
        selectedItem = selectedStory.items.find(
          m => m.mosObjectId === requestData.mosObjectObjectId
        );
      }

      if (selectedItem) {
        requestData.itemId = selectedItem.itemId;
      }
    }

    if (!options.callback) {
      dispatch(createAction(INSERT_MOS_OBJECT_REQUEST)());
    }

    return apiInputMosObject(requestData, createMethod, options.sendFromCamio)
      .then(response => {
        const mosXmlPayload = getMosXmlPayload(response);
        if (mosXmlPayload) {
          if (options.callback) {
            return apiInputMosObject(
              {
                mosObjectObjectId: mosXmlPayload.objId
              },
              "getmosobjectid"
            ).then(id => dispatch(options.callback(id)));
          }
          if (!options.sendFromCamio && !options.suppressContainerMessage) {
            sendMosMessageToContainer(mosXmlPayload.mosXml);
          }

          if (options.additionalMosXml && options.additionalMosXml.length > 0) {
            options.additionalMosXml.forEach(mosObject => {
              sendMosMessageToContainer(mosObject.mosXml);

              const start = mosObject.mosXml.match(/<sAVsom>(.*)<\/sAVsom>/)[1];
              const duration = mosObject.mosXml.match(
                /<sAVeom>(.*)<\/sAVeom>/
              )[1];

              return apiGetMosObjectTemplateTemplate(
                mosObject.mosObjectId
              ).then(json => {
                const templateDefinition = TemplateUtils.parseTemplateDefinition(
                  json.templateDefinition,
                  json.templateReplaceables
                );

                const selectedChannel =
                  templateDefinition.properties["default channel"];
                const innerRequestData = {
                  mosObjectObjectId: mosObject.mosObjectId || "",
                  ncsId: getNcsIdFromNcsAppInfo(mosObject.mosXml),
                  replaceables: (templateDefinition.textReplaceables || [])
                    .concat(templateDefinition.mediaReplaceables)
                    .map(r => ({
                      id: r.id,
                      name: r.name,
                      type: r.type,
                      value: r.value,
                      provider: r.provider
                    })),
                  templateData: {
                    channelId: selectedChannel ? selectedChannel.id : "",
                    channelName: selectedChannel,
                    inTime: start,
                    outTime: duration,
                    slug: templateDefinition.properties["mos abstract"]
                  },
                  templateId: templateDefinition.uuid
                };
                return apiInputMosObject(innerRequestData, "update");
              });
            });
          }

          dispatch(
            insertMosObjectSuccess(
              mosXmlPayload,
              requestData,
              options.suppressContainerMessage
            )
          );

          return apiGetMosObjectTemplateTemplatePreviewPath({
            mosObjectObjectId: mosXmlPayload.objId
          }).then(response2 =>
            dispatch(
              createAction(MOS_OBJECT_PREVIEW_PATH_RETRIEVED)({
                objId: mosXmlPayload.objId,
                previewImgUrl: response2
              })
            )
          );
        }
        return dispatch(insertMosObjectFailed());
      })
      .catch(error => {
        console.error(error);
        return dispatch(insertMosObjectFailed());
      });
  };
}

function getMosXmlPayload(mosXml) {
  let mosXmlMassaged = null;
  if (mosXml && mosXml.length > 0) {
    const json = xmlToJson(mosXml, { mergeAttrs: false });
    if (json.generatemosobject.mos[0]) {
      mosXmlMassaged = jsonToXml(json.generatemosobject.mos[0], {
        rootName: "mos",
        headless: true
      });
    }

    const parser = new DOMParser();
    const mosXmlParsed = parser.parseFromString(mosXmlMassaged, "text/xml");
    return {
      objId: mosXmlParsed.evaluate(
        "//mosObj/objID/text()",
        mosXmlParsed,
        null,
        XPathResult.STRING_TYPE,
        null
      ).stringValue,
      title: mosXmlParsed.evaluate(
        "//mosAbstract/text()",
        mosXmlParsed,
        null,
        XPathResult.STRING_TYPE,
        null
      ).stringValue,
      mosXml: mosXmlMassaged
    };
  }
  return null;
}

function loadEditor(id, isMosObject) {
  return dispatch => {
    if (isMosObject) {
      return dispatch(getMosObject(id, AppMode.EDIT));
    }
  };
}

function getMosObject(mosObjectId, modeSwitch) {
  return dispatch =>
    apiGetMosObject(mosObjectId)
      .then(mosObject => {
        dispatch(
          createAction(ASSET_SELECTED)({
            selectedAsset: mosObject,
            editing: true
          })
        );
        dispatch(
          handleTemplateDefinitionReceived(
            mosObject.templateDefinitionXml,
            mosObject.templateReplaceables || []
          )
        );
        dispatch(loadAutofillData());
        if (modeSwitch) {
          dispatch(appActions.setAppMode(modeSwitch));
        }

        return true;
      })
      .catch(error => {
        console.log(error);
      });
}

function getAutofilledValue(
  selectedReplaceableName,
  autofilledReplaceable,
  isColumnAutofill,
  autofillData,
  autofillColumnData,
  autofillIndex,
  value = ""
) {
  if (
    isColumnAutofill &&
    autofillColumnData[selectedReplaceableName][autofillIndex] != null
  ) {
    return autofillColumnData[selectedReplaceableName][autofillIndex].columns[
      autofilledReplaceable.properties.autofillDataColumnIndex
    ].value;
  }

  if (
    !isColumnAutofill &&
    autofillData[selectedReplaceableName][autofillIndex] != null
  ) {
    return autofillData[selectedReplaceableName][autofillIndex][
      autofilledReplaceable.name
    ].value;
  }

  if (autofilledReplaceable.name === selectedReplaceableName) {
    return value;
  }

  return "";
}

export function updateAutofillValues(
  autofillIndex,
  name,
  value,
  autofillGroup
) {
  return async (dispatch, getState) => {
    const {
      autofillData,
      autofillColumnData,
      templateDefinition
    } = getState().asset;
    const currentContextId = getState().assets.contextId;
    const isColumnAutofill =
      autofillColumnData[name] != null && autofillColumnData[name].length > 0;
    let filter = r => r.name === name;
    if (isColumnAutofill) {
      const dataListName = templateDefinition.textReplaceables.find(
        r => r.name === name
      ).properties.autofillDataListName;
      if (autofillGroup !== "-1") {
        filter = r =>
          r.properties.autofillDataListName === dataListName &&
          r.properties.autofillGroup === autofillGroup;
      } else {
        filter = r => r.properties.autofillDataListName === dataListName;
      }
    } else if (
      autofillData &&
      autofillData[name] &&
      autofillData[name][autofillIndex]
    ) {
      filter = r =>
        Object.prototype.hasOwnProperty.call(
          autofillData[name][autofillIndex],
          r.name
        );
    }

    const allReplaceables = (templateDefinition.textReplaceables || [])
      .concat(templateDefinition.mediaReplaceables || [])
      .filter(filter);
    const updatedReplaceables = await Promise.all(
      allReplaceables.map(r => {
        const autofillValue = getAutofilledValue(
          name,
          r,
          isColumnAutofill,
          autofillData,
          autofillColumnData,
          autofillIndex,
          value
        );
        if (r.type === "text") {
          return {
            ...r,
            value: autofillValue
          };
        }
        return apiGetAutofillMedia(
          currentContextId,
          autofillValue,
          r.type
        ).then(data => ({
          ...r,
          url: data.thumbnailUrl,
          value: data.id
        }));
      })
    );

    dispatch(
      createAction(UPDATE_AUTOFILL_REPLACEABLE_VALUES)({ updatedReplaceables })
    );
  };
}

export function createDeficientAsset(replaceableId, data) {
  return (dispatch, getState) =>
    apiCreateDeficientAsset(
      getState().assets.contextId,
      data.type,
      data.title,
      getState().auth.user.username || "",
      data.guid
    ).then(response => {
      if (response) {
        const deficientAssetJson = xmlToJson(response);
        return dispatch(
          updateReplaceableValue({
            id: replaceableId,
            value: deficientAssetJson.deficientasset.id[0],
            name: data.name,
            type: data.type,
            provider: "local",
            url: "images/waiting_delivery_thumbnail.png"
          })
        );
      }
      return Promise.resolve();
    });
}

export function endEditAsset() {
  return createAction(END_EDIT_ASSET)();
}

export const getPrimeStyles = (contextId, primeStyle) => dispatch => {
  if (!contextId) {
    return;
  }
  dispatch(createAction(REQUEST_PRIME_STYLES)());
  return apiGetPrimeStyles(contextId)
    .then(response => {
      if (response) {
        response.sort((a, b) => (a.displayIndex > b.displayIndex ? 1 : -1));
        return dispatch(
          createAction(PRIME_STYLES_RECEIVED)({
            primeStyles: response,
            primeStyle
          })
        );
      }
    })
    .catch(error => {
      console.error(error);
      dispatch(createAction(REQUEST_PRIME_STYLES_FAILED)());
      return Promise.reject(error);
    });
};

export const changePrimeStyle = primeStyle => dispatch =>
  dispatch(createAction(PRIME_STYLE_CHANGED)({ primeStyle }));

export const saveState = () => (dispatch, getState) => {
  const currentState = getState().asset;
  return {
    primeStyle: currentState.primeStyle
  };
};

export const restoreState = state => (dispatch, getState) => {
  dispatch(getPrimeStyles(state.assets.contextId, state.asset.primeStyle));
  return dispatch(createAction(RESTORE_STATE)({ ...state.asset }));
};

export const actions = {
  toggleReplaceableUseDataSource,
  selectAsset,
  loadAsset,
  loadChannelList,
  loadAutomationTransitionList,
  updateReplaceableValue,
  updateTemplateProperty,
  createMosObjectFromExisting,
  createNewMosObject,
  replaceMosObjectWithNew,
  updateMosObject,
  openMosObject,
  openClipperMosObject,
  generatePreview,
  selectReplaceable,
  endEditAsset,
  updateSelectedReplaceable,
  updateAutofillValues,
  insertFromClipper,
  createDeficientAsset,
  clearAsset,
  swapTemplate,
  createMosObjectFromDragDrop,
  loadEditor,
  getPrimeStyles,
  changePrimeStyle
};



// WEBPACK FOOTER //
// ./app/actions/asset.js